import React, { useState, useEffect } from 'react'
import axios from 'axios'
import Tooltip from '@mui/material/Tooltip'
import Modal from '../shared/modal'
import { t } from '../../i18n'
import FlashNotification from '../shared/flashNotification'

const scope = 'admin.settings'

const EnqueueActionModal = ({ action, performAction, onClose }) => {
  const { name, asap_allowed, boiler_nr } = action

  const actionName = t(`action_names.${name}`, { scope, boiler_nr })

  return (
    <Modal headerText={actionName} onClose={onClose}>
      <div className='mb-5'>
        {t('select_priority_prompt', { scope, action_name: actionName })}
      </div>
      <div>
        <button
          className='btn btn-primary'
          onClick={() => performAction(action)}
        >
          {t('add_to_queue', { scope })}
        </button>
        {asap_allowed &&
          <button className='btn btn-secondary ms-2' onClick={() => performAction(action, { asap: true })}>
            {t('perform_asap', { scope })}
          </button>
        }
        <button className='btn btn-secondary ms-2' onClick={onClose}>
          {t('cancel')}
        </button>
      </div>
    </Modal>
  )
}

interface SettingsPermissions {
  canUpdate: boolean
  canEnableSmsNotifications: boolean
  canEnableSimulatorStressTesting: boolean
  canOpenLockers: boolean
  canGenerateServiceTasks: boolean
}

type SetFlashMessage = (type: string, message: string) => void

interface SettingsProps {
  setFlashMessage: SetFlashMessage
  canUpdate: boolean
  canEnableSmsNotifications: boolean
  canEnableSimulatorStressTesting: boolean
}

const Settings = ({ setFlashMessage, canUpdate, canEnableSmsNotifications, canEnableSimulatorStressTesting }: SettingsProps) => {
  const [settings, setSettings] = useState<any>({})
  const [savedSettings, setSavedSettings] = useState<any>({})

  const isDirty = JSON.stringify(settings) !== JSON.stringify(savedSettings)

  useEffect(() => {
    fetchSettings()
  }, [])

  useEffect(() => {
    if (!isDirty) return

    const warnBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warnBeforeUnload)
    return () => window.removeEventListener('beforeunload', warnBeforeUnload)
  }, [isDirty])

  const fetchSettings = () => {
    axios.get('/admin/settings/get_settings').then(({ data }) => {
      setSettings(data)
      setSavedSettings(data)
    })
  }

  const saveSettings = () => {
    axios.put('/admin/settings/update', { setting: settings }).then(({ data }) => {
      setSettings(data)
      setSavedSettings(data)
      setFlashMessage('success', t('settings_updated', { scope }))
    }).catch(({ response }) => {
      setFlashMessage('danger', response.data.error)
    })
  }

  const renderCheckbox = (field, enabled = true, show = true) => {
    const handleChange = (e) => {
      const { checked } = e.target
      setSettings({ ...settings, [field]: checked })
    }

    if (!show) return null

    return (
      <fieldset className='form-group boolean'>
        <div className='form-check'>
          <input
            id={field}
            name={field}
            type='checkbox'
            checked={!!settings[field]}
            onChange={handleChange}
            className='form-check-input pointer'
            disabled={!enabled}
          />
          <Tooltip title={enabled ? '' : 'You do not have permission to edit this setting'} placement='top' arrow>
            <label className='form-check-label boolean pointer' htmlFor={field}>
              {t(`activerecord.attributes.setting.${field}`)}
            </label>
          </Tooltip>
        </div>
      </fieldset>
    )
  }

  const renderNumberInput = (field, { min = 0 }: { min?: number } = {}) => {
    const handleChange = (e) => {
      const { value } = e.target
      setSettings({ ...settings, [field]: value === '' ? '' : Number(value) })
    }

    return (
      <div className='form-group mt-3'>
        <label htmlFor={field}>
          {t(`activerecord.attributes.setting.${field}`)}
        </label>
        <input
          id={field}
          name={field}
          type='number'
          min={min}
          value={settings[field] ?? ''}
          onChange={handleChange}
          className='form-control inverted'
        />
      </div>
    )
  }

  return <>
    <h4 className='mb-4'>{t('settings', { scope })}</h4>

    <form>
      {renderCheckbox('order_scheduling_enabled')}
      {renderCheckbox('service_session_in_progress')}
      {renderCheckbox('command_log_estimate_calculation_activated')}
      {renderCheckbox('sms_notifications_enabled', canEnableSmsNotifications)}
      {renderCheckbox(
        'simulator_stress_testing_enabled',
        canEnableSimulatorStressTesting,
        canEnableSimulatorStressTesting
      )}
      {renderCheckbox('skip_robot_commands_in_service_app')}
      {renderCheckbox('menu_auto_copy_enabled')}
      {renderNumberInput('boiler_wait_tolerance_seconds')}
      {renderNumberInput('pause_robot_before_boiler_wait_seconds')}

      {canUpdate &&
        <button
          type='button'
          className='btn btn-primary mt-2'
          onClick={saveSettings}
        >
          {t('save')}
        </button>
      }
    </form>
  </>
}

type RobotAction = {
  name: string
  available: boolean
  asap_allowed: boolean
  destructive?: boolean
  boiler_nr?: number
  locker_nr?: number
  stand_nr?: number
}

interface ActionsProps {
  setFlashMessage: SetFlashMessage
  canOpenLockers: boolean
  canGenerateServiceTasks: boolean
}

const Actions = ({ setFlashMessage, canOpenLockers, canGenerateServiceTasks }: ActionsProps) => {
  const [mainActions, setMainActions] = useState<RobotAction[]>([])
  const [boilerActions, setBoilerActions] = useState<Record<number, RobotAction[]>>([])
  const [resetActions, setResetActions] = useState<RobotAction[]>([])
  const [lockerActions, setLockerActions] = useState<Record<number, RobotAction[]>>([])
  const [plateStandActions, setPlateStandActions] = useState<Record<number, RobotAction[]>>([])
  const [serviceSessionActions, setServiceSessionActions] = useState<RobotAction[]>([])
  const [selectedAction, setSelectedAction] = useState<RobotAction | null>(null)

  useEffect(() => {
    fetchRobotActions()
  }, [])

  const fetchRobotActions = () => {
    axios.get('/admin/settings/get_robot_actions').then(({ data }) => {
      setMainActions(data.main_actions)
      setBoilerActions(data.boiler_actions)
      setResetActions(data.reset_actions)
      setLockerActions(data.locker_actions)
      setServiceSessionActions(data.service_session_actions)
      setPlateStandActions(data.plate_stand_actions)
    })
  }

  const performAction = (action, { asap = false } = {}) => {
    const { name, boiler_nr } = action

    axios.post(`/admin/settings/perform_action`, {
      action_name: name,
      asap,
      boiler_nr
    }).then(({ data }) => {
      setFlashMessage('success', t(`action_enqueued.${name}`, { scope, boiler_nr }))
      setSelectedAction(null)
      fetchRobotActions()
    }).catch(({ response }) => {
      setFlashMessage('danger', response.data.error)
    })
  }

  const performResetAction = (action, callback = () => {}) => {
    const { name } = action

    axios.post(`/admin/settings/${name}`).then(({ data }) => {
      setFlashMessage('success', t(`reset_performed.${name}`, { scope }))
      fetchRobotActions()
    }).catch(({ response }) => {
      setFlashMessage('danger', response.data.error)
    }).finally(callback)
  }

  const updateLocker = (action, lockerNr, callback) => {
    axios.post(`/admin/settings/${action.name}`, { locker_number: lockerNr }).then(({ data }) => {
      setFlashMessage('success', t(`locker_action_performed.${action.name}`, { locker_nr: lockerNr, scope }))
      fetchRobotActions()
    }).catch(({ response }) => {
      setFlashMessage('danger', response.data.error)
    }).finally(callback)
  }

  const closeAllLockers = (callback) => {
    axios.post("/admin/settings/close_all_lockers").then(({ data }) => {
      setFlashMessage('success', t(`locker_action_performed.close_all_lockers`, { scope }))
      fetchRobotActions()
    }).catch(({ response }) => {
      setFlashMessage('danger', response.data.error)
    }).finally(callback)
  }

  const openAllLockers = (callback) => {
    axios.post("/admin/settings/open_all_lockers").then(({ data }) => {
      setFlashMessage('success', t(`locker_action_performed.open_all_lockers`, { scope }))
      fetchRobotActions()
    }).catch(({ response }) => {
      setFlashMessage('danger', response.data.error)
    }).finally(callback)
  }

  const resetPlateStand = (action, standNr, callback) => {
    axios.post(`/admin/settings/${action.name}`, { stand_nr: standNr }).then(({ data }) => {
      setFlashMessage('success', t(`plate_stand_action_performed.${action.name}`, { stand_nr: standNr, scope }))
      fetchRobotActions()
    }).catch(({ response }) => {
      setFlashMessage('danger', response.data.error)
    }).finally(callback)
  }

  const generateServiceTasks = () => {
    axios.post('/admin/service_tasks/generate_service_task_executions').then(({ data }) => {
      setFlashMessage('success', t('service_tasks_generated', { scope }))
      fetchRobotActions()
    }).catch(({ response }) => {
      setFlashMessage('danger', response.data.error)
    })
  }

  const executeServiceSessionAction = (action: RobotAction, callback = () => {}) => {
    const confirmText = t(`service_session_action_confirm.${action.name}`, { scope, defaultValue: t('global.confirm') })
    if(!(confirm(confirmText))) return

    axios.post(`/admin/settings/${action.name}`).then(({ data }) => {
      setFlashMessage('success', t(`service_session_action_performed.${action.name}`, { scope, count: data?.canceled_count }))
      fetchRobotActions()
    }).catch(({ response }) => {
      setFlashMessage('danger', response.data.error)
    }).finally(callback)
  }

  const resetCooker = (action: RobotAction, callback = () => {}) => {
    const { boiler_nr } = action

    axios.post('/admin/settings/reset_cooker', { slot_number: boiler_nr }).then(() => {
      setFlashMessage('success', t('reset_cooker_performed', { scope, boiler_nr }))
      fetchRobotActions()
    }).catch(({ response }) => {
      setFlashMessage('danger', response.data.error)
    }).finally(callback)
  }

  return <>
    <h4 className='mb-4'>{t('actions', { scope })}</h4>

    {/* <h6 className='mt-4 mb-3'>Robot</h6>

    <div className='settings-action-group'>
      {mainActions.map(action => (
        <button
          key={action.name}
          className='btn btn-secondary'
          onClick={() => setSelectedAction(action)}
          disabled={!action.available}
        >
          {t(action.name, { scope })}
        </button>
      ))}
    </div> */} {/* Currently no main actions while new robot version does not support */}

    <div className='row'>
      {Object.entries(boilerActions).map(([boilerNr, actions]) =>
        <div className='col' key={boilerNr}>
          <h6 className='mt-5 mb-3'>{`Boiler #${boilerNr}`}</h6>
          <div className='d-flex'>
            {actions.map(action => (
              action.name === 'reset_cooker' ? (
                <ResetCookerButton
                  key={action.name}
                  action={action}
                  resetCooker={resetCooker}
                />
              ) : (
                <button
                  key={action.name}
                  className='btn btn-secondary w-100'
                  onClick={() => setSelectedAction(action)}
                  disabled={!action.available}
                >
                  {t(action.name, { scope })}
                </button>
              )
            ))}
          </div>
        </div>
      )}
    </div>

    <div className='row'>
      {Object.entries(lockerActions).map(([lockerNr, actions]) =>
        <div className='col' key={lockerNr}>
          <h6 className='mt-5 mb-3'>{`Locker #${lockerNr}`}</h6>
          <div className='d-flex'>
            {actions.map(action => (
              <UpdateLockerButton
                action={action}
                lockerNr={lockerNr}
                updateLocker={updateLocker}
              />
            ))}
          </div>
        </div>
      )}
    </div>

    {canOpenLockers &&
      <div className='row'>
        <div className='col' key="all_lockers">
          <h6 className='mt-5 mb-3'>Open/close all lockers</h6>
          <div className='d-flex'>
            <OpenAllLockersButton openAllLockers={openAllLockers} />
            <CloseAllLockersButton closeAllLockers={closeAllLockers} />
          </div>
        </div>
      </div>
    }

    {Object.keys(plateStandActions).length > 0 && <>
      <h6 className='mt-5 mb-3'>Plate Stands (Disabled/Faulty)</h6>
      <div className='row'>
        {Object.entries(plateStandActions).map(([standNr, actions]) =>
          <div className='col-auto' key={standNr}>
            <div className='d-flex'>
              {actions.map(action => (
                <ResetPlateStandButton
                  key={`${action.name}-${standNr}`}
                  action={action}
                  standNr={standNr}
                  resetPlateStand={resetPlateStand}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </>}

    <h6 className='mt-5 mb-3'>Reset</h6>

    <div className='settings-action-group'>
      {resetActions.map(action =>
        <ResetActionButton
          key={action.name}
          action={action}
          performResetAction={performResetAction}
        />
      )}
    </div>

    {selectedAction &&
      <EnqueueActionModal
        action={selectedAction}
        performAction={performAction}
        onClose={() => setSelectedAction(null)}
      />
    }

    {canGenerateServiceTasks && <>
      <h6 className='mt-4 mb-3'>Service tasks</h6>

      <div className='settings-action-group'>
          <button
            key='generate_service_tasks'
            className='btn btn-secondary'
            onClick={generateServiceTasks}
          >
            Generate service tasks for today
          </button>
      </div>
    </>}

    {serviceSessionActions.length > 0 &&
      <h6 className='mt-4 mb-3'>Mobile Service app service session</h6>
    }

    <div className='settings-action-group'>
      {serviceSessionActions.map(action =>
        <ServiceSessionActionButton
          key={action.name}
          serviceSessionAction={action}
          executeServiceSessionAction={executeServiceSessionAction}
        />
      )}
    </div>
  </>
}

const ResetActionButton = ({ action, performResetAction }) => {
  const [loading, setLoading] = useState(false)

  const handleClick = () => {
    if(!(confirm(t('global.confirm')))) return

    setLoading(true)
    performResetAction(action, () => setLoading(false))
  }

  return (
    <button
      key={action.name}
      className='btn btn-danger me-2'
      onClick={handleClick}
      disabled={!action.available || loading}
    >
      {t(action.name, { scope })}
      {loading &&
        <div className='spinner-border spinner-border-sm ms-2 me-2'/>
      }
    </button>
  )
}

const ResetCookerButton = ({ action, resetCooker }) => {
  const [loading, setLoading] = useState(false)

  const handleClick = () => {
    if(!(confirm(t('global.confirm')))) return

    setLoading(true)
    resetCooker(action, () => setLoading(false))
  }

  return (
    <button
      key={action.name}
      className='btn btn-danger w-100'
      onClick={handleClick}
      disabled={!action.available || loading}
    >
      {t(action.name, { scope })}
      {loading &&
        <div className='spinner-border spinner-border-sm ms-2 me-2'/>
      }
    </button>
  )
}

const UpdateLockerButton = ({ action, lockerNr, updateLocker }) => {
  const [loading, setLoading] = useState(false)

  const handleClick = () => {
    setLoading(true)
    updateLocker(action, lockerNr, () => setLoading(false))
  }

  return (
    <button
      key={action.name}
      className='btn btn-secondary w-100'
      onClick={handleClick}
      disabled={!action.available || loading}
    >
      {t(action.name, { scope })}
      {loading &&
        <div className='spinner-border spinner-border-sm ms-2 me-2'/>
      }
    </button>
  )
}

const ResetPlateStandButton = ({ action, standNr, resetPlateStand }) => {
  const [loading, setLoading] = useState(false)

  const handleClick = () => {
    if(!(confirm(t('global.confirm')))) return

    setLoading(true)
    resetPlateStand(action, standNr, () => setLoading(false))
  }

  return (
    <button
      key={`${action.name}-${standNr}`}
      className='btn btn-secondary me-2'
      onClick={handleClick}
      disabled={!action.available || loading}
    >
      {t(action.name, { scope })} #{standNr}
      {loading &&
        <div className='spinner-border spinner-border-sm ms-2 me-2'/>
      }
    </button>
  )
}

const OpenAllLockersButton = ({ openAllLockers }) => {
  const [loading, setLoading] = useState(false)

  const handleClick = () => {
    setLoading(true)
    openAllLockers(() => setLoading(false))
  }

  return (
    <button
      key='open_all_lockers'
      className='btn btn-secondary'
      onClick={handleClick}
    >
      Open all lockers
      {loading &&
        <div className='spinner-border spinner-border-sm ms-2 me-2'/>
      }
    </button>
  )
}

const CloseAllLockersButton = ({ closeAllLockers }) => {
  const [loading, setLoading] = useState(false)

  const handleClick = () => {
    setLoading(true)
    closeAllLockers(() => setLoading(false))
  }

  return (
    <button
      key='close_all_lockers'
      className='btn btn-secondary ms-2'
      onClick={handleClick}
    >
      Close all lockers
      {loading &&
        <div className='spinner-border spinner-border-sm ms-2 me-2'/>
      }
    </button>
  )
}

const ServiceSessionActionButton = ({ serviceSessionAction, executeServiceSessionAction }:
   { serviceSessionAction: RobotAction, executeServiceSessionAction: (action: RobotAction, callback?: () => void) => void }) => {
  const [loading, setLoading] = useState(false)

  const handleClick = () => {
    setLoading(true)
    executeServiceSessionAction(serviceSessionAction, () => setLoading(false))
  }

  return (
    <button
      className={serviceSessionAction.destructive ? 'btn btn-danger ms-2' : 'btn btn-secondary'}
      onClick={handleClick}
      disabled={loading || !serviceSessionAction.available}
    >
      {t(serviceSessionAction.name, { scope })}
      {loading &&
        <div className='spinner-border spinner-border-sm ms-2 me-2'/>
      }
    </button>
  )
}

const SettingsScreen = ({ canUpdate, canEnableSmsNotifications, canEnableSimulatorStressTesting, canOpenLockers, canGenerateServiceTasks }: SettingsPermissions) => {
  const [flashNotification, setFlashNotification] = useState<{
    type: string
    message: string
    open: boolean
  } | null>(null)

  const setFlashMessage = (type, message, timeOut = 3000) => {
    setFlashNotification({ type, message, open: true })
    setTimeout(() => setFlashNotification(null), timeOut)
  }

  return (
    <div className='card card-robis'>
      <Settings
        setFlashMessage={setFlashMessage}
        canUpdate={canUpdate}
        canEnableSmsNotifications={canEnableSmsNotifications}
        canEnableSimulatorStressTesting={canEnableSimulatorStressTesting}
      />
      <hr className='my-5'/>
      <Actions
        setFlashMessage={setFlashMessage}
        canOpenLockers={canOpenLockers}
        canGenerateServiceTasks={canGenerateServiceTasks}
      />
      {flashNotification?.open &&
        <FlashNotification
          type={flashNotification.type}
          message={flashNotification.message}
        />
      }
    </div>
  )
}

export default SettingsScreen
